import { useState } from 'react'

import { useAuthToken, verifyAndDecodeJwtToken } from '@surglogs-app/auth'
import { useHandler } from '@surglogs-app/hooks'
import {
  useAuthTokenExchangePostMutation,
  AuthTokenExchangePostPayload,
} from '@surglogs-app/network'

export const useTokenExchangeHandler = ({
  handleLoginError,
  onSuccess,
}: {
  handleLoginError: (error: unknown) => void
  onSuccess?: (userId: number, token: string) => void
}) => {
  const [isPending, setIsPending] = useState(false)
  const { setToken } = useAuthToken()

  const {
    mutateAsync: exchangeCodeForTokenMutateAsync,
    isSuccess,
    reset,
  } = useAuthTokenExchangePostMutation({ opts: { throwOnError: false } })

  const tokenExchangeHandler = useHandler(
    async (variables: AuthTokenExchangePostPayload) => {
      try {
        setIsPending(true)
        const {
          authentication_result: {
            access_token: accessToken,
            refresh_token: refreshToken,
            expires_in: expiresIn,
            jwks_url: jwksUrl,
          },
        } = await exchangeCodeForTokenMutateAsync(variables)

        const verifiedDecodedToken = await verifyAndDecodeJwtToken(
          accessToken,
          jwksUrl,
        )

        if (!verifiedDecodedToken) {
          throw new Error('Failed to verify or decode JWT token')
        }

        setToken(accessToken, refreshToken, expiresIn)
        onSuccess?.(Number(verifiedDecodedToken.surglogs_user_id), accessToken)
      } catch (error) {
        handleLoginError(error)
      } finally {
        setIsPending(false)
      }
    },
  )

  return { tokenExchangeHandler, isPending, isSuccess, reset }
}
